import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import { ProfileCard } from '@/components/QuickSearch/ProfileCard/ProfileCard';
import type { ItemFieldDef } from '@/core/dcr/fields/types';
import {
	type HcoOption,
	useHcoAffiliationsSearch
} from '@/core/dcr/hooks/useHcoAffiliationsSearch';
import type { ParentHcoDisplay } from '@/core/dcr/schemas/collections.schema';
import { getAffiliationCaption } from '@/utils/affiliations/getAffiliationCaption';
import { formatAddress } from '@/utils/formatters/formatAddress';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { useFieldLabel } from '../_hooks/useFieldLabel';
import { hasValue } from '../_utils/hasValue';

import { hcoSearchItemInputStyles as sx } from './HcoSearchItemInput.styles';

type Props = {
	name: string;
	displayName: string;
	def: ItemFieldDef;
};

export const HcoSearchItemInput = ({ name, displayName, def }: Props) => {
	const { t } = useTranslation();
	const { countryId } = useDcrFormContext();
	const fieldLabel = useFieldLabel();
	const { control, setValue } = useFormContext();

	const display = useWatch({ control, name: displayName }) as
		ParentHcoDisplay | null | undefined;

	const { options, handleChange, loading } = useHcoAffiliationsSearch({
		entityCountryId: countryId
	});

	const caption = useMemo(
		() =>
			getAffiliationCaption({
				hcoType: display?.hcoType ?? undefined,
				location: display?.location ?? undefined
			}),
		[display]
	);

	const handleSelect = (item: HcoOption | null) => {
		if (!item) {
			setValue(displayName, null, { shouldDirty: true });
			setValue(name, '', { shouldDirty: true, shouldValidate: true });
			return;
		}

		setValue(
			displayName,
			{
				name: item.hcoNameCda_V ?? '',
				hcoType: item.hcoType?.name ?? '',
				location: formatAddress(item.addresses?.[0])
			} satisfies ParentHcoDisplay,
			{ shouldDirty: true }
		);
		setValue(name, item.vid_V, { shouldDirty: true, shouldValidate: true });
	};

	return (
		<Controller
			name={name}
			control={control}
			render={({ field, fieldState }) => (
				<FormAutocompleteField<HcoOption>
					isRequired={def.required}
					isAsync
					id={name}
					name={field.name}
					label={fieldLabel(def)}
					placeholder={t('Search for HCO')}
					options={options}
					loading={loading}
					onChange={handleChange}
					handleOnDropdownItemSelect={handleSelect}
					defaultValue={display?.name}
					indicateError={!!fieldState.error}
					isDirty={hasValue(field.value)}
					renderOption={option => (
						<ProfileCard
							profile={option}
							withIcon={false}
							withFullAddress
							customSx={sx.profileCard}
						/>
					)}
					caption={caption}
				/>
			)}
		/>
	);
};
